import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { APP_URL, DEMO_CTA_HREF } from '@/config';
import { Brand } from './v2/ui';
import { navLinks } from './v2/site-content';
export function Navbar({
  theme = 'light',
  onToggleTheme,
}: {
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    setOpen(false);
  }, [location]);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const outside = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const desktop = window.matchMedia('(min-width: 1000px)');
    const resize = () => {
      if (desktop.matches) setOpen(false);
    };
    document.addEventListener('keydown', close);
    document.addEventListener('pointerdown', outside);
    desktop.addEventListener('change', resize);
    return () => {
      document.removeEventListener('keydown', close);
      document.removeEventListener('pointerdown', outside);
      desktop.removeEventListener('change', resize);
    };
  }, [open]);
  return (
    <header
      className="site-header"
      ref={navRef}
      data-scrolled={scrolled}
      data-open={open}
    >
      <div className="container nav-row">
        <Brand light={theme === 'dark'} />
        <nav className="desktop-navigation" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              aria-current={
                location.pathname === link.href ? 'page' : undefined
              }
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="nav-actions">
          {onToggleTheme && (
            <button
              className="icon-button theme-toggle"
              onClick={onToggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
          )}
          <a className="login-link" href={`${APP_URL}/login`}>
            Log in
          </a>
          <a className="button button-primary nav-sales" href={DEMO_CTA_HREF}>
            Try Live Demo
          </a>
          <button
            ref={toggleRef}
            className="icon-button menu-toggle"
            onClick={() => setOpen((value) => !value)}
            aria-label={open ? 'Close navigation' : 'Open navigation'}
            aria-expanded={open}
            aria-controls="mobile-navigation"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <nav
          id="mobile-navigation"
          className="mobile-navigation container"
          aria-label="Mobile navigation"
        >
          {navLinks.map((link) => (
            <Link key={link.label} to={link.href}>
              {link.label}
            </Link>
          ))}
          <a href={`${APP_URL}/login`}>Log in</a>
          <a className="button button-primary" href={DEMO_CTA_HREF}>
            Try Live Demo
          </a>
        </nav>
      )}
    </header>
  );
}
